import React, { useMemo } from "react";
import { copy } from "shared/assets";
import { Menu } from "shared/assets/icons";
import { Icon, MutedDarkText, TinyText } from "shared/styles";
import { ActionDropdown, IActionDropdownItem } from "shared/components";

import {
  TeaserContainer,
  TeaserTitle,
  TeaserContent,
  TeaserButtonContainer,
  TeaserContainerProps,
  ActionDropdownContainer,
} from "./styles";
import { colors } from "shared/styles/constants";

export interface TeaserProps {
  actions?: IActionDropdownItem[];
  onClick?: () => void;
  disabled?: boolean;
}

interface MainTeaserProps extends TeaserProps, TeaserContainerProps {
  title?: string;
  titleIcon?: React.ComponentType<any>;
  subTitle?: string[] | string;
  subTitleComponent?: JSX.Element;
  url?: string;
  "data-testid"?: string;
}

export const MainTeaser: React.FC<MainTeaserProps> = ({
  title,
  titleIcon,
  subTitle,
  subTitleComponent,
  url,
  actions,
  onClick,
  children,
  hasHorizontalContent,
  ...rest
}) => {
  const actionSelectProps = useMemo(
    () => ({
      label: copy.menu.open,
      options: actions || [],
      triggerIcon: Menu,
      dataTestId: "teaser_action_select",
      wrapOptions: true,
    }),
    [actions]
  );

  const renderInner = (isInteractive: boolean = true) => (
    <>
      <TeaserContent
        as={url && isInteractive ? "a" : "div"}
        hasHorizontalContent={hasHorizontalContent}
        {...(url &&
          isInteractive && { href: url, target: "_blank", rel: "noreferrer" })}
      >
        {title && (
          <TeaserTitle>
            {titleIcon && (
              <Icon as={titleIcon} size="xl" color={colors.grey5} data-icon />
            )}
            <span>{title}</span>
          </TeaserTitle>
        )}

        {subTitle && (
          <MutedDarkText as={TinyText}>
            {typeof subTitle === "string" ? subTitle : subTitle.join(" • ")}
          </MutedDarkText>
        )}
        {subTitleComponent}
        {!hasHorizontalContent && <div>{children}</div>}
      </TeaserContent>
      {hasHorizontalContent && <div>{children}</div>}
    </>
  );

  return (
    <>
      {onClick ? (
        <TeaserButtonContainer
          isInteractive
          type="button"
          onClick={onClick}
          hasHorizontalContent={hasHorizontalContent}
          {...rest}
        >
          {renderInner(false)}
        </TeaserButtonContainer>
      ) : (
        <TeaserContainer
          isInteractive={!!url}
          hasHorizontalContent={hasHorizontalContent}
          {...rest}
        >
          {renderInner()}
          {!!actions?.length && (
            <ActionDropdownContainer>
              <ActionDropdown {...actionSelectProps} placement="bottom-end" />
            </ActionDropdownContainer>
          )}
        </TeaserContainer>
      )}
    </>
  );
};
